import { useState, useRef, useEffect } from "react";
import { Layers, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useGroupTickets, type SourceOrder } from "@/hooks/useGroupTickets";

interface GroupTicketsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentType: "table" | "command";
  currentNumber: string;
  currentOrderId: string | null;
  onSuccess: () => void;
}

export function GroupTicketsDialog({
  open,
  onOpenChange,
  currentType,
  currentNumber,
  currentOrderId,
  onSuccess,
}: GroupTicketsDialogProps) {
  const [searchType, setSearchType] = useState<"table" | "command">(currentType);
  const [searchNumber, setSearchNumber] = useState("");
  const [sources, setSources] = useState<SourceOrder[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { findActiveOrder, groupOrders, isSearching, isGrouping } = useGroupTickets();

  useEffect(() => {
    if (open) {
      setSources([]);
      setSearchNumber("");
      setSearchType(currentType);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, currentType]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const handleAdd = async () => {
    if (!searchNumber.trim() || !currentOrderId) return;

    // Check if already added
    if (sources.some((s) => s.type === searchType && s.number === searchNumber.trim())) {
      return;
    }

    const result = await findActiveOrder(searchType, searchNumber.trim(), currentOrderId);
    if (result) {
      setSources((prev) => [...prev, result]);
      setSearchNumber("");
      inputRef.current?.focus();
    }
  };

  const handleRemove = (index: number) => {
    setSources((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGroup = async () => {
    if (!currentOrderId || sources.length === 0) return;
    const success = await groupOrders(currentOrderId, sources);
    if (success) {
      onOpenChange(false);
      onSuccess();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const totalItems = sources.reduce((s, src) => s + src.itemCount, 0);
  const totalValue = sources.reduce((s, src) => s + src.total, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--pdv-surface-panel,#0b1020)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text,#c8dff5)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--pdv-text,#c8dff5)]">
            <Layers className="h-5 w-5 text-[var(--pdv-accent,#00aaff)]" />
            Agrupar Contas
          </DialogTitle>
          <DialogDescription className="text-[var(--pdv-text-subtle,#4a7abf)]">
            {currentType === "table" ? "Mesa" : "Comanda"} {currentNumber} — adicione
            mesas ou comandas para agrupar
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setSearchType("table")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                searchType === "table"
                  ? "bg-[var(--pdv-highlight,#0a3a6e)] text-[var(--pdv-accent-light,#4dd0ff)] border border-[var(--pdv-highlight-border,#0077cc)]"
                  : "bg-[var(--pdv-surface,#111829)] text-[var(--pdv-text-muted,#7aabdd)] border border-[var(--pdv-border,#1e2d52)]"
              }`}
            >
              Mesa
            </button>
            <button
              onClick={() => setSearchType("command")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                searchType === "command"
                  ? "bg-[var(--pdv-highlight,#0a3a6e)] text-[var(--pdv-accent-light,#4dd0ff)] border border-[var(--pdv-highlight-border,#0077cc)]"
                  : "bg-[var(--pdv-surface,#111829)] text-[var(--pdv-text-muted,#7aabdd)] border border-[var(--pdv-border,#1e2d52)]"
              }`}
            >
              Comanda
            </button>
          </div>

          <div className="flex gap-2">
            <Input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              placeholder={`Nº da ${searchType === "table" ? "mesa" : "comanda"}`}
              value={searchNumber}
              onChange={(e) => setSearchNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              className="bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text,#c8dff5)] placeholder:text-[var(--pdv-text-subtle,#4a7abf)]"
            />
            <Button
              onClick={handleAdd}
              disabled={!searchNumber.trim() || isSearching || !currentOrderId}
              className="gap-1 bg-[var(--pdv-highlight,#0a3a6e)] border border-[var(--pdv-highlight-border,#0077cc)] text-[var(--pdv-accent-light,#4dd0ff)] hover:bg-[var(--pdv-highlight-border)]/80"
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Sources list */}
        {sources.length > 0 && (
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {sources.map((source, index) => (
              <div
                key={`${source.type}-${source.number}`}
                className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--pdv-surface,#111829)] border border-[var(--pdv-border,#1e2d52)]"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-[var(--pdv-text,#c8dff5)]">
                    {source.type === "table" ? "Mesa" : "Comanda"} {source.number}
                  </span>
                  <span className="text-xs text-[var(--pdv-text-subtle,#4a7abf)]">
                    {source.itemCount} {source.itemCount === 1 ? "item" : "itens"} •{" "}
                    {formatCurrency(source.total)}
                  </span>
                </div>
                <button
                  onClick={() => handleRemove(index)}
                  className="p-1.5 rounded-md text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Summary */}
            <div className="flex items-center justify-between pt-2 border-t border-[var(--pdv-border,#1e2d52)]">
              <span className="text-xs text-[var(--pdv-text-subtle,#4a7abf)]">
                Total a agrupar: {totalItems} itens
              </span>
              <span className="text-sm font-bold text-[var(--pdv-success,#00e676)]">
                {formatCurrency(totalValue)}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1 bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text-muted,#7aabdd)] hover:bg-[var(--pdv-highlight-hover)]"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            className="flex-1 gap-1.5 bg-[var(--pdv-success-bg,#0a3d1e)] border border-[var(--pdv-success-border,#00c853)] text-[var(--pdv-success,#00e676)] hover:bg-[var(--pdv-success-border)]/30 font-bold"
            onClick={handleGroup}
            disabled={sources.length === 0 || isGrouping}
          >
            {isGrouping ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Layers className="h-4 w-4" />
            )}
            Agrupar Tudo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
